import {computed, inject, provide, reactive, ref} from 'vue';

import AuthLoginComponent from '@/components/auth/blocks/login/login.vue';
import EventPriceBoxComponent from '@/components/events/blocks/event-price-box/event-price-box.vue';
import AuthLoginRegisterComponent from '@/components/auth/blocks/login-register/login-register.vue';
import PurchaseProductSingleComponent
  from '@/components/purchase/blocks/product-single/product-single.vue';
import EventProductSelectComponent
  from '@/components/events/blocks/event-product-select/event-product-select.vue';
import PaymentBillingDetailsComponent
  from '@/components/payment/blocks/billing-details/billing-details.vue';
import PurchaseSummaryComponent from '@/components/purchase/blocks/summary/summary.vue';
import PurchaseCouponComponent from '@/components/purchase/blocks/coupon/coupon.vue';
import PurchaseDisclaimerEventComponent
  from '@/components/purchase/blocks/disclaimer/event/event.vue';
import {library} from '@fortawesome/fontawesome-svg-core';
import {faRectangleXmark} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';
import SkeletonLoader from '@/components/content/views/skeleton-loader/skeleton-loader.vue';

library.add(faRectangleXmark);

/**
 * Event Price Box View
 * @module Events Checkout View
 *
 * @prop { Config } config Component configuration
 * @prop { User } [user] Authenticated user details
 * @prop { eventType } [String] Type of event to display
 *
 */

export default {
  name: 'EventPriceBoxView',
  props: {
    config: {
      type: Object,
      required: false,
      default: {},
    },
    user: {
      type: Object,
      required: false,
      default: null
    },
    event: {
      type: Number,
      required: true,
      default: null
    },
    activeProduct: {
      type: String,
      required: false,
    },
    showCoupon: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    AuthLoginComponent,
    EventPriceBoxComponent,
    AuthLoginRegisterComponent,
    PurchaseProductSingleComponent,
    EventProductSelectComponent,
    PaymentBillingDetailsComponent,
    PurchaseCouponComponent,
    PurchaseSummaryComponent,
    PurchaseDisclaimerEventComponent,
    FontAwesomeIcon,
    SkeletonLoader,
  },
  setup(props) {
    let config = inject('config');
    config = {...config, ...props.config};
    provide('config', config);

    const authProvider = inject('authProvider');
    const eventProvider = inject('eventProvider');
    const customerProvider = inject('customerProvider');
    const subscriptionProvider = inject('subscriptionProvider');

    const state = reactive({
      showNotification: ref(false),
      notification: ref(null),
      user: computed(() => authProvider().getUser),
      hasUser: computed(() => authProvider().hasUser),
      isLoadingUser: computed(() => authProvider().isLoading),
      hasCookie: computed(() => authProvider().hasCookie),
      showLogin: ref(false),
      userEvents: computed(() => eventProvider().getUserEvents),
      event: ref(null),
      confirmDelete: ref(null),
      terms: ref(false),
      selected: computed(() => subscriptionProvider().getProduct),
      coupon: computed(() => subscriptionProvider().getCoupon),
      payment: computed(() => subscriptionProvider().getProfile),
      preview: computed(() => subscriptionProvider().getPreview),
      hasProfiles: computed(() => customerProvider().hasProfiles),
      profiles: computed(() => customerProvider().getProfiles),
      defaultProfile: computed(() => customerProvider().getDefaultProfile),
      selectedProfile: computed(() => subscriptionProvider().getProfile),
      canSubmit: computed(() => {
        return state.selected && state.terms && (state.payment || (subscriptionProvider().isCreatingProfile && true === subscriptionProvider().isValidProfile));
      }),
      showCheckout: ref(false),
      inPersonOnly: computed(() => {
        return state.event.in_person_product_handle && !state.event.product_handle;
      }),
      productOptions: computed(() => {
        const returnArray = [];
        const object = {};

        if (state.inPersonOnly) {
          object.title = state.event.products.in_person.name;
          object.product = {
            handle: state.event.products.in_person.handle,
            price: state.event.products.in_person.price_in_cents
          };
        } else {
          object.title = state.event.name;
          object.description = inThePast(state.event.ends_at) ? 'Replay' : '';
          const convertedPrice = state.event.price * 100;
          object.product = {handle: state.event.product_handle, price: convertedPrice};
          if (state.event.in_person_product_handle) {
            object.title = state.event.products.virtual.name;
            const inPerson = {};
            inPerson.title = state.event.products.in_person.name;
            inPerson.product = {
              handle: state.event.products.in_person.handle,
              price: state.event.products.in_person.price_in_cents
            };
            returnArray.push(inPerson);
          }
        }
        returnArray.push(object);
        return returnArray;
      }),
      loginErrorMessage: ref(null),
      hasOptin: ref(false),
      optin: ref(false),
      hasInPerson: computed(() => {
        return state.event && !inThePast(state.event.ends_at) && state.event.in_person_product_handle;
      }),
      formattedProducts: computed(() => {
        if (state.hasInPerson && !state.inPersonOnly) {
          state.event.products.in_person.description = state.event.in_person_product_description;
          state.event.products.virtual.description = state.event.product_description;
          state.event.products.in_person.option_name = 'In Person Ticket';
          state.event.products.virtual.option_name = 'Virtual Ticket';
        }
        return state.event.products;
      }),
      selectedProduct: ref(null),
    });

    const hasResale = () => {
      if (state.event.resale.price) {
        if (state.event.resale.dates.ends_at) {
          return !inThePast(state.event.resale.dates.ends_at);
        }
        return true;
      }
      return false;
    };

    const eventReplayForSale = () => {
      return state.event.replay.sellable;
    };

    const resaleEligible = () => {
      if (userHasEvent(state.userEvents)) {
        if (inThePast(state.event.ends_at)) {
          if (userEventIsExpired(state.userEvents)) {
            if (eventReplayForSale()) {
              if (hasResale()) {
                return true;
              }
            }
          }
        }
      }

      return false;
    };

    const onSale = () => {
      if (state.event.sale.price) {
        if (state.event.sale.dates.ends_at) {
          return !inThePast(state.event.sale.dates.ends_at);
        }
        return true;
      }
      return false;
    };

    const userHasEvent = (userEvents) => {
      if (userEvents.length) {
        return userEvents.some(events => events.event.id === state.event.id);
      }
      return false;
    };

    const getUserEventExpiration = (userEvents) => {
      if (userHasEvent(userEvents)) {
        const userEvent = userEvents.find(events => events.event.id === state.event.id);
        return userEvent.access.event.expires_at;
      }
      return null;
    };

    const userEventIsExpired = (userEvents) => {
      const expires = getUserEventExpiration(userEvents);
      return expires ? inThePast(expires) : false;
    };

    const inThePast = (datetime) => {
      const date = new Date(datetime);
      const now = new Date();

      return (now > date);
    };

    const isCompleted = (section) => {
      const completed = [];
      if (state.selected) {
        completed.push(1);
      }
      if (state.payment) {
        completed.push(2);
      }
      if (state.terms) {
        completed.push(3);
      }

      return completed.includes(section);
    };

    const missingFields = () => {
      if (!state.selected) {
        return 'Please select the plan you would like to purchase!';
      }
      if (!state.user) {
        return 'Please login or signup to continue!';
      }
      if (!state.payment && !subscriptionProvider().isCreatingProfile) {
        return 'Please select the payment method you would like to use!';
      }
      if (true !== subscriptionProvider().isValidProfile && subscriptionProvider().isCreatingProfile) {
        return subscriptionProvider().isValidProfile;
      }
      if (!state.terms) {
        return 'Please read and accept the terms & conditions!';
      }

      return false;
    };

    const updateProduct = async (handle) => {
      state.showNotification = true;
      state.notification = 'Updating Product...';

      const response = await subscriptionProvider().updateProduct(handle,
        state.productOptions,
        config);

      await handleResponse(response);

      if ('success' === response.status) {
        state.expanded = 2;
      }
      return response;
    };

    const updateCoupon = async (code = null) => {
      state.showNotification = true;
      state.notification = 'Validating Promotion Code...';

      if (!code) {
        if (state.hasUser) {
          if (resaleEligible()) {
            code = state.event.resale.coupon;
          } else if (onSale()) {
            code = state.event.sale.coupon;
          }
        } else if (onSale()) {
          code = state.event.sale.coupon;
        }
      }

      const response = await subscriptionProvider().updateCoupon(code, config);

      await handleResponse(response);
      return response;
    };

    const loadCustomer = async (customer, token) => {
      state.showNotification = true;
      state.notification = 'Loading Customer...';
      const {data} = await customerProvider().loadCustomer(customer, token, config);
      const {profiles} = data.data;

      state.showNotification = true;
      state.notification = 'Updating Customer...';
      let response = await subscriptionProvider().updateCustomer(customer, token, config);
      if (customerProvider().hasCustomerMembership) {
        state.showNotification = true;
        state.notification = 'Loading Membership...';
        const membership = await customerProvider().getCustomerMembership;
        response = await subscriptionProvider().updateMembership(membership, config);
        await handleResponse(response);
      } else if (customerProvider().hasCustomer) {
        state.showNotification = false;
        state.notification = '';
      }

      if (1 === profiles.length && 'active' === profiles[0].status) {
        activatePayment(profiles[0]);
      }

    };

    const register = async (data) => {
      state.showNotification = true;
      state.notification = 'Creating Account...';

      const {firstname, lastname, email, username, password, confirm} = data;
      let response = await authProvider().register(firstname,
        lastname,
        email,
        username,
        password,
        confirm,
        config);
      await handleResponse(response);

      if ('success' === response.status) {
        const {data} = response.data;
        response = await loadCustomer(data.customer.id, data.token);
      }
    };

    const createPayment = async (details) => {
      state.showNotification = true;
      state.notification = 'Creating Payment Method...';

      const response = await customerProvider().createPayment(details, state.user.token, config);

      await handleResponse(response);
      if ('success' === response.status) {
        await activatePayment(response.data.data);
        await purchase();
      }
    };

    const activatePayment = async (details) => {
      state.showNotification = true;
      state.notification = 'Updating Payment Method...';

      const response = await subscriptionProvider().activateProfile(details.id, config);

      await handleResponse(response);
    };

    const updatePayment = async (details) => {
      state.showNotification = true;
      state.notification = 'Updating Payment Profile...';

      const response = await customerProvider().updatePayment(details, state.user.token, config);

      await handleResponse(response);
    };

    const removePayment = async (profile) => {
      state.showNotification = true;
      state.notification = 'Removing Payment Method...';

      if (customerProvider().hasCustomerMembership) {
        const membership = await customerProvider().getCustomerMembership;
        const response = await subscriptionProvider().removeProfile(membership, profile.id, config);
        await handleResponse(response);
      } else {
        const response = await customerProvider().removePayment(profile.id,
          state.user.token,
          config);
        await handleResponse(response);
      }

      const {id, token} = state.user;
      let response = await authProvider().loadUser(id, token, config);
      await handleResponse(response);

      if ('success' === response.status) {
        const {data} = response.data;
        response = await loadCustomer(data.customer.id, data.token);
        await handleResponse(response);
      }
    };

    const purchase = async () => {
      if (subscriptionProvider().isCreatingProfile) {
        const event = new Event('create');
        document.querySelector('#chargify-form').dispatchEvent(event);
        return false;
      }

      if (state.optin) {
        await subscriptionProvider().updateOptin(true, config);
      }

      state.showNotification = true;
      state.notification = 'Pardon the wait. The checkout process can take a bit. Please do not refresh or close the page...';

      const response = await subscriptionProvider().purchase(config);

      await handleResponse(response);
    };

    const toggleExpanded = async (number) => state.expanded = number;

    const next = () => {
      if (state.selected && state.user && state.payment) {
        return state.expanded = 3;
      }
      if (state.selected) {
        return state.expanded = 2;
      }
    };

    const handleResponse = async (response) => {
      if (response.status === 'error' || response.success === false) {
        state.showNotification = true;
        state.notification = response.data.message || response.data.data.message || response.data.data;
      } else if (response.data.redirect && response.data.redirect.url || response.data.data.redirect && response.data.data.redirect.url) {
        state.showNotification = true;
        state.notification = 'Success! Redirecting...';
      } else {
        state.showNotification = false;
        state.notification = null;
      }
    };

    const handleLoginResponse = async (response) => {
      if (response.status === 'error' || response.success === false) {
        state.showLogin = true;
        state.notification = response.data.message || response.data.data.message || response.data.data;
      } else {
        state.showLogin = false;
        state.notification = null;
        document.body.classList.remove('koc-has-overlay');
      }
    };

    const handleInteriorLoginResponse = async (response) => {
      if (response.status === 'error' || response.success === false) {
        state.loginErrorMessage = response.data.message || response.data.data.message || response.data.data;
      } else {
        state.showLogin = false;
        state.notification = null;
        document.body.classList.remove('koc-has-overlay');
      }
    };

    const loadUserEvents = async (user, token) => {
      state.showNotification = true;
      state.notification = 'Checking User Events...';
      let response = await eventProvider().loadUserEvents(user, token, config);
      if (response.status === 'error' || response.success === false) {
        state.showNotification = true;
        state.notification = response.data.message || response.data.data.message || response.data.data;
      } else {
        state.showNotification = false;
        state.showLogin = false;
      }

    };

    const loadConferenceEvent = async (event, config) => {
      state.showNotification = true;
      state.notification = 'Loading Events...';
      let response = await eventProvider().loadEvent(event, config);
      if (response.status === 'error' || response.success === false) {
        state.showNotification = true;
        state.notification = response.data.message || response.data.data.message || response.data.data;
      } else {
        state.event = response.data.data;
        state.hasOptin = state.event.optin.enabled;
        state.showNotification = false;
        state.notification = null;
      }

    };

    const loadUser = async (user) => {
      state.showNotification = true;
      state.notification = 'Loading User...';

      const {id, token} = user;
      let response = await authProvider().loadUser(id, token, config);
      await handleResponse(response);

      if ('success' === response.status) {
        const {data} = response.data;
        response = await loadUserEvents(data.id, data.token);
        response = await loadCustomer(data.customer.id, data.token);
      }

    };

    const login = async (data) => {
      state.loginErrorMessage = null;
      state.notification = 'Validating Credentials...';

      const {username, password} = data;
      const response = await authProvider().login(username, password, config);
      await handleLoginResponse(response);

      if ('success' === response.status) {
        const {data} = response.data;
        await loadUser(data);
        state.showLogin = false;
        await updateCoupon();
      }
    };

    const interiorLogin = async (data) => {
      state.loginErrorMessage = null;
      state.notification = 'Validating Credentials...';

      const {username, password} = data;
      const response = await authProvider().login(username, password, config);
      await handleInteriorLoginResponse(response);

      if ('success' === response.status) {
        const {data} = response.data;
        await loadUser(data);
        state.showLogin = false;
        await updateCoupon();
      }
    };

    const setSelectedProduct = async () => {
      state.selectedProduct = state.event.products.in_person;
    }

    const clearNotification = async => state.showNotification = false;

    const initialize = async () => {

      if (props.user && !state.hasUser) {
        await loadUser(props.user);
      } else {
        if (state.hasCookie) {
          return;
        }
        const cookie = authProvider().loadUserCookie(config);
        if (cookie) {
          await loadUser(cookie);
        }
      }

      await loadConferenceEvent(props.event, config);

      if (state.inPersonOnly) {
        await updateProduct(state.event.in_person_product_handle);
        if(state.event.in_person_coupon) {
          await updateCoupon(state.event.in_person_coupon);
        } else {
          await updateCoupon();
        }
      } else {
        await updateProduct(state.event.product_handle);
        await updateCoupon();
      }

    };

    initialize();

    document.addEventListener('koc-login', async (event) => {
      if (state.hasUser && event.detail.user.id !== state.user.id) {
        await eventProvider().removeUserEvents(config);
        await authProvider().logout(config);
        state.hasAccess = false;
      }
      return await loadUser(event.detail.user);
    });

    document.addEventListener('koc-logout', async () => {
      if (state.hasUser) {
        await eventProvider().removeUserEvents(config);
        return await authProvider().logout(config);
      }
    });

    document.addEventListener('koc-show-checkout', async () => {
      this.showCheckout();
    });

    return {
      login,
      clearNotification,
      loadUser,
      loadConferenceEvent,
      register,
      updateProduct,
      updateCoupon,
      createPayment,
      activatePayment,
      updatePayment,
      removePayment,
      purchase,
      toggleExpanded,
      isCompleted,
      missingFields,
      next,
      interiorLogin,
      state
    };
  },
  methods: {
    showLogin: async function () {
      this.state.showLogin = true;
      document.body.classList.add('koc-has-overlay');
    },
    closeLogin: async function () {
      this.state.showLogin = false;
      document.body.classList.remove('koc-has-overlay');
    },
    showCheckout: async function () {
      this.state.showCheckout = true;
    },
    switchProduct: async function (product) {
      await this.updateProduct(product);
      if (!this.state.hasInPerson) {
        return;
      }

      if (this.state.event.in_person_product_handle !== product) {
        await this.updateCoupon();
        return;
      }

      if (this.state.event.in_person_coupon) {
        await this.updateCoupon(this.state.event.in_person_coupon);
      }
    },
  },
};
